/* prettier-ignore-file */
import React, { useRef, useEffect, useState } from "react";
import { View, StyleSheet, TouchableOpacity, Text } from "react-native";

const SignaturePad = ({ onSignatureChange, width = 300, height = 150, fullWidth = false, isEmpty = true }) => {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Create canvas element for web (only once on mount)
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    
    // In React Native Web, the ref points to the DOM element
    const domNode = container.nodeType ? container : container._nativeNode || container;
    if (!domNode) return;
    
    const resolvedWidth = fullWidth ? domNode.offsetWidth || width : width;
    
    const canvas = document.createElement("canvas");
    canvas.width = resolvedWidth;
    canvas.height = height;
    canvas.style.width = fullWidth ? "100%" : `${resolvedWidth}px`;
    canvas.style.height = `${height}px`;
    canvas.style.border = isEmpty ? "1px solid #C4453D" : "1px solid #DDE3E2";
    canvas.style.borderRadius = "8px";
    canvas.style.backgroundColor = isEmpty ? "#FBEAE9" : "#FFFFFF";
    canvas.style.touchAction = "none";
    canvas.style.cursor = "crosshair";
    canvas.style.display = "block";
    
    domNode.appendChild(canvas);
    canvasRef.current = canvas;

    return () => {
      try {
        if (domNode && canvas && domNode.contains(canvas)) {
          domNode.removeChild(canvas);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [width, height, fullWidth]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0F1A19";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      // Scale from CSS pixels to bitmap pixels. With fullWidth the canvas is styled
      // width:100% while its bitmap stays at whatever width it had on mount, so any
      // resize or device rotation makes those two diverge — without this the stroke
      // lands offset from the finger.
      const scaleX = rect.width ? canvas.width / rect.width : 1;
      const scaleY = rect.height ? canvas.height / rect.height : 1;
      const source = e.touches && e.touches.length > 0 ? e.touches[0] : e;
      return {
        x: (source.clientX - rect.left) * scaleX,
        y: (source.clientY - rect.top) * scaleY,
      };
    };

    const startDrawing = (e) => {
      e.preventDefault();
      setIsDrawing(true);
      const coords = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
    };

    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const coords = getCoordinates(e);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      setHasSignature(true);
    };

    const stopDrawing = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      setIsDrawing(false);
      const signature = canvas.toDataURL("image/png");
      setHasSignature(true);
      // Update canvas style when signature is added
      canvas.style.border = "1px solid #DDE3E2";
      canvas.style.backgroundColor = "#FFFFFF";
      onSignatureChange?.(signature);
    };

    // Mouse events
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseleave", stopDrawing);

    // Touch events
    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("touchmove", draw);
    canvas.addEventListener("touchend", stopDrawing);
    canvas.addEventListener("touchcancel", stopDrawing);

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
      canvas.removeEventListener("touchcancel", stopDrawing);
    };
  }, [isDrawing, onSignatureChange]);

  // Update canvas style when isEmpty prop changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    if (isEmpty) {
      canvas.style.border = "1px solid #C4453D";
      canvas.style.backgroundColor = "#FBEAE9";
    } else {
      canvas.style.border = "1px solid #DDE3E2";
      canvas.style.backgroundColor = "#FFFFFF";
    }
  }, [isEmpty]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    // Update canvas style when signature is cleared
    canvas.style.border = "1px solid #C4453D";
    canvas.style.backgroundColor = "#FBEAE9";
    onSignatureChange?.(null);
  };

  return (
    <View style={styles.container}>
      <View style={styles.canvasWrapper} ref={canvasContainerRef} />
      {hasSignature && (
        <TouchableOpacity dataSet={{ hover: "lift" }} onPress={clearSignature} style={styles.clearButton}>
          <Text style={styles.clearButtonText}>Clear</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
  canvasWrapper: {
    width: "100%",
    alignItems: "center",
  },
  canvas: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    touchAction: "none",
    cursor: "crosshair",
    display: "block",
  },
  instructionText: {
    fontSize: 12,
    color: "#8A9794",
    marginTop: 8,
    textAlign: "center",
    fontStyle: "italic",
  },
  clearButton: {
    position: "absolute",
    top: -35,
    right: 0,
    padding: 6,
    backgroundColor: "#C4453D",
    borderRadius: 4,
  },
  clearButtonText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
});

export default SignaturePad;

